import React, {useRef, useEffect, useState} from 'react';
import Button from "@mui/material/Button";

export const BuildingState = ({state, userId, setConfirmAction}) => {

    const mapName = state.players[userId].mapName;
    const canvasRef = useRef(null);
    const [selectedTile, setSelectedTile] = useState(null); // {name, rotation}
    const [pointerPos, setPointerPos] = useState(null);
    const [hoveredPlacement, setHoveredPlacement] = useState(null);
    const [placedTile, setPlacedTile] = useState(undefined);
    const buildingImgCache = useRef({});
    const bonusImgCache = useRef({});
    const blockingBuilding2ImgCache = useRef(null);
    const possibleBuildingImgCache = useRef(null);

    // --- Hex/grid helpers (single source of truth) ---
    const HEX = {
        R: 71,
        ORIG_CENTER_X: 84,
        ORIG_CENTER_Y_FROM_BOTTOM: 207,
    };

    const getMapTransform = (canvas, img) => {
        const rect = canvas.getBoundingClientRect();
        const cssSize = Math.max(0, Math.floor(rect.width));
        const scale = Math.min(cssSize / img.width, cssSize / img.height);
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        const offsetX = (cssSize - drawW) / 2;
        const offsetY = (cssSize - drawH) / 2;
        return {rect, cssSize, scale, offsetX, offsetY};
    };

    const hexToOriginalPx = (img, i, j) => {
        const r = HEX.R;
        const ORIG_CENTER_Y = img.height - HEX.ORIG_CENTER_Y_FROM_BOTTOM;
        const x = HEX.ORIG_CENTER_X + 1.5 * r * i;
        const y = ORIG_CENTER_Y - (Math.sqrt(3) * r) * j + (i % 2 === 1 ? Math.sqrt(3) * 0.5 * r : 0);
        return {x, y};
    };

    const originalPxToCanvasCss = (t, p) => ({x: t.offsetX + p.x * t.scale, y: t.offsetY + p.y * t.scale});
    const canvasCssToOriginalPx = (t, p) => ({x: (p.x - t.offsetX) / t.scale, y: (p.y - t.offsetY) / t.scale});

    const nearestHex = (img, origPt) => {
        // Find nearest (i,j) on the infinite grid by searching around an estimated i.
        const r = HEX.R;
        const ORIG_CENTER_Y = img.height - HEX.ORIG_CENTER_Y_FROM_BOTTOM;

        const iEst = Math.round((origPt.x - HEX.ORIG_CENTER_X) / (1.5 * r));

        let best = {i: 0, j: 0, d2: Infinity};
        for (let di = -2; di <= 2; di++) {
            const i = iEst + di;
            const yOffset = (i % 2 === 1 ? Math.sqrt(3) * 0.5 * r : 0);
            const jEst = Math.round((ORIG_CENTER_Y + yOffset - origPt.y) / (Math.sqrt(3) * r));

            for (let dj = -2; dj <= 2; dj++) {
                const j = jEst + dj;
                const c = hexToOriginalPx(img, i, j);
                const dx = c.x - origPt.x;
                const dy = c.y - origPt.y;
                const d2 = dx * dx + dy * dy;
                if (d2 < best.d2) best = {i, j, d2};
            }
        }
        return {i: best.i, j: best.j};
    };

    const findPlaceTileActionAt = (name, i, j, rotation) => {
        const actList = (state.actions && state.actions[userId]) || [];
        const a = actList.find(a0 => {
            return a0.i === i && a0.j === j && a0.nbRotations === rotation && a0.name === name;
        });
        return a || null;
    };

    const getBuildingImg = (building) => {
        if (!building.name) return null;
        if (buildingImgCache.current[building.name]) return buildingImgCache.current[building.name];
        const img = new Image();
        img.src = `/public/arknova/buildings/building_${building.name.toLowerCase()}${building.name.startsWith("ENCLOS") && !building.occupied?'_empty':''}.png`;
        img.onload = () => {
            drawCanvas();
        };
        buildingImgCache.current[building.name] = img;
        return img;
    };

    const getBonusImg = (bonus) => {
        if (!bonus.bonusName) return null;
        if (bonusImgCache.current[bonus.bonusName]) return bonusImgCache.current[bonus.bonusName];
        const img = new Image();
        img.src = `/public/arknova/bonus/bonus_${bonus.bonusName.toLowerCase()}.png`;
        img.onload = () => {
            drawCanvas();
        };
        bonusImgCache.current[bonus.bonusName] = img;
        return img;
    };

    const availableBuildings = [...new Set(state.actions[userId].filter(a => a.class === "action.ActionPlaceTile" && a.ok).map(a => a.name))];
    const alreadyPlaced = state.players[userId].zooMap.buildings;

    // Cache the loaded image and separate image loading from canvas drawing to avoid flicker
    const imgRef = React.useRef(null);

    // Use RAF for smoother drawing. renderRAF holds the request id.
    const renderRAF = useRef(null);
    const drawCanvasImmediate = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Make the canvas square using the component width (100%) and set pixel size for DPR
        const parent = canvas.parentElement;
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        const cssSize = Math.max(0, Math.floor(rect.width));
        const dpr = window.devicePixelRatio || 1;

        // Set CSS size so canvas occupies full width and is square
        canvas.style.width = cssSize + 'px';
        canvas.style.height = cssSize + 'px';

        // Set actual pixel size for sharp rendering
        canvas.width = Math.max(1, Math.floor(cssSize * dpr));
        canvas.height = Math.max(1, Math.floor(cssSize * dpr));
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const img = imgRef.current;
        // Clear canvas
        ctx.clearRect(0, 0, cssSize, cssSize);
        if (!img) return;

        // Compute scale to fit the image entirely inside the square (contain)
        const scale = Math.min(cssSize / img.width, cssSize / img.height);
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        const offsetX = (cssSize - drawW) / 2;
        const offsetY = (cssSize - drawH) / 2;

        ctx.drawImage(img, offsetX, offsetY, drawW, drawH);

        if(selectedTile?.name){
            Object.values(state.players[userId].possiblesHexForBuildings[selectedTile.name]).forEach(hex => {
                let bimg;
                const i = parseInt(hex.i);
                const j = parseInt(hex.j);
                if (possibleBuildingImgCache.current) {
                    bimg = possibleBuildingImgCache.current;
                } else {
                    bimg = new Image();
                    bimg.src = `/public/arknova/buildings/possible_building.png`;
                    bimg.onload = () => {
                        drawCanvas();
                    };
                    possibleBuildingImgCache.current = bimg;
                }
                const orig = hexToOriginalPx(img, i, j);
                const c = originalPxToCanvasCss({offsetX, offsetY, scale}, orig);
                const imgW = 120 * scale* bimg.width / bimg.height; // tune as needed
                const imgH = 120 * scale ; // tune as needed
                ctx.save();
                ctx.translate(c.x, c.y);
                ctx.drawImage(bimg, - imgW / 2, - imgH / 2, imgW, imgH);
                ctx.restore();
            });
        }

        // draw bonus and blocking
        Object.keys(state.players[userId].zooMap.bonuses).forEach(i => Object.keys(state.players[userId].zooMap.bonuses[i]).forEach(j => {
            const bonus = state.players[userId].zooMap.bonuses[i][j];
            const bimg = getBonusImg(bonus);
            if (!bimg || !bimg.complete) return;
            const orig = hexToOriginalPx(img, i, j);
            const c = originalPxToCanvasCss({offsetX, offsetY, scale}, orig);
            const imgW = 70 * scale* bimg.width / bimg.height; // tune as needed
            const imgH = 70 * scale ; // tune as needed
            ctx.save();
            ctx.translate(c.x, c.y);
            ctx.drawImage(bimg, - imgW / 2, - imgH / 2, imgW, imgH);
            if(bonus.value && (bonus.value > 1 || bonus.bonusName === "REPUTATION")){
                ctx.fillStyle = "white";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.font = `${Math.floor(imgH * 0.7)}px Arial`;
                ctx.fillText("" + bonus.value, 0, 0);
            }
            ctx.restore();
        }));

        Object.keys(state.players[userId].zooMap.blockingBuilding2).forEach(iKey => Object.keys(state.players[userId].zooMap.blockingBuilding2[iKey].values).forEach(jKey => {
            let bimg;
            const i = parseInt(iKey);
            const j = parseInt(state.players[userId].zooMap.blockingBuilding2[iKey].values[jKey].value);
            if (blockingBuilding2ImgCache.current) {
                bimg = blockingBuilding2ImgCache.current;
            } else {
                bimg = new Image();
                bimg.src = `/public/arknova/bonus/icon_requirement_building_2.png`;
                bimg.onload = () => {
                    drawCanvas();
                };
                blockingBuilding2ImgCache.current = bimg;
            }
            const orig = hexToOriginalPx(img, i, j);
            const c = originalPxToCanvasCss({offsetX, offsetY, scale}, orig);
            const imgW = 70 * scale* bimg.width / bimg.height; // tune as needed
            const imgH = 70 * scale ; // tune as needed
            ctx.save();
            ctx.translate(c.x, c.y);
            ctx.drawImage(bimg, - imgW / 2, - imgH / 2, imgW, imgH);
            ctx.restore();
        }));

        // Draw placed tiles (persisted) on top of map
        if (alreadyPlaced && alreadyPlaced.length > 0) {
            alreadyPlaced.forEach(pt => {
                const bimg = getBuildingImg(pt);
                if (!bimg || !bimg.complete) return;
                const orig = hexToOriginalPx(img, pt.i, pt.j);
                const c = originalPxToCanvasCss({offsetX, offsetY, scale}, orig);
                const imgW = 570 * scale; // tune as needed
                const imgH = 500 * scale; // tune as needed

                ctx.save();
                ctx.translate(c.x, c.y);
                ctx.rotate(Math.PI / 3 * pt.nbRotations);
                ctx.drawImage(bimg, - imgW / 2, - imgH / 2, imgW, imgH);
                ctx.restore();
            });
        }

    };

    const drawCanvas = () => {
        if (renderRAF.current) cancelAnimationFrame(renderRAF.current);
        renderRAF.current = requestAnimationFrame(() => {
            drawCanvasImmediate();
            renderRAF.current = null;
        });
    };

    // Load image once per mapName and cache it
    useEffect(() => {
        const safeMapName = (mapName || '').toString().toLowerCase();
        const img = new Image();
        img.src = `/public/arknova/maps/map_${safeMapName}.png`;
        img.onload = () => {
            imgRef.current = img;
            // draw once loaded
            drawCanvas();
        };
        img.onerror = () => {
            imgRef.current = null;
        };
        return () => {
            imgRef.current = null;
        };
    }, [mapName]);

    // Redraw when inputs change, do not mutate state here
    useEffect(() => {
        drawCanvas();
    }, [mapName, selectedTile, pointerPos, hoveredPlacement]);

    // cancel RAF on unmount
    useEffect(() => {
        return () => {
            if (renderRAF.current) cancelAnimationFrame(renderRAF.current);
        };
    }, []);

    const placeOrMoveSelectedTileAtEvent = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (!selectedTile?.name) return;

        const img = imgRef.current;
        if (!img) return;

        const rect = canvas.getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;

        const t = getMapTransform(canvas, img);
        const orig = canvasCssToOriginalPx(t, {x: px, y: py});
        const snapped = nearestHex(img, orig);

        const origSnapped = hexToOriginalPx(img, snapped.i, snapped.j);
        const c = originalPxToCanvasCss(t, origSnapped);

        const imgW = 570 * t.scale;
        const imgH = 500 * t.scale;

        const rotation = placedTile?.rotation || 0;
        const action = findPlaceTileActionAt(selectedTile.name, snapped.i, snapped.j, rotation);

        setPlacedTile({
            name: selectedTile.name,
            i: snapped.i,
            j: snapped.j,
            top: c.y - imgH / 2,
            left: c.x - imgW / 2,
            width: imgW,
            height: imgH,
            action,
            rotation
        });

        drawCanvas();
    };

    const handlePlacedTileClick = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (!selectedTile?.name) return;

        const img = imgRef.current;
        if (!img) return;

        const rectMap = canvas.getBoundingClientRect();
        const rect = e.currentTarget.getBoundingClientRect();
        const relX = e.clientX - (rect.left + rect.width / 2); // px inside the image
        const relY = e.clientY - (rect.top + rect.height / 2);  // px inside the image
        console.log(relX, relY, rect.width, rect.height);

        if(relY > - rectMap.height * 500 / 1024 / 6 && relY < rectMap.height * 500 / 1024 / 6 && (relX <  - 1.5 * rectMap.width * 570 / 1024 / 5 || relX > 1.5 * rectMap.width * 570 / 1024 / 5)) {
            const deltaRotation = relX < 0 ? 1 : -1;
            setPlacedTile(prev => {
                const nextRotation = (prev.rotation + deltaRotation + 6) % 6;
                return {
                    ...prev,
                    rotation: nextRotation,
                    action: findPlaceTileActionAt(prev.name, prev.i, prev.j, nextRotation)
                };
            });
        } else {
            const el = e.currentTarget;
            el.style.pointerEvents = "none";
            const below = document.elementFromPoint(e.clientX, e.clientY);
            if(below){
                below.dispatchEvent(new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    clientX: e.clientX,
                    clientY: e.clientY,
                    screenX: e.screenX,
                    screenY: e.screenY,
                    button: e.button,
                }));
            }
            el.style.pointerEvents = "auto";
        }
    };

    // Clicking the placed tile toggles "move mode" for that tile
    const handlePlacedTileMouseDown = () => {
        if (!placedTile?.name) return;
        setSelectedTile({name: placedTile.name, rotation: placedTile.rotation});
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <h2>Vous pouvez construire des bâtiments</h2>
            {/* Top: canvas area for map + existing buildings */}
            <div style={{flex: 1, position: 'relative', border: 'none', display: 'flex', alignItems: 'stretch'}}>
                <canvas
                    ref={canvasRef}
                    onClick={placeOrMoveSelectedTileAtEvent}
                    style={{width: '100%', height: '100%', display: 'block', background: '#ffffff00', cursor: selectedTile ? 'crosshair' : 'default'}}
                />

                {placedTile && (
                    <img
                        src={`/public/arknova/buildings/building_${placedTile.name.toLowerCase()}.png`}
                        alt={placedTile.name}
                        draggable={false}
                        onMouseDown={handlePlacedTileMouseDown}
                        onClick={handlePlacedTileClick}
                        onError={(ev) => console.warn('building img failed to load', placedTile.name, ev?.currentTarget?.src)}
                        style={{
                            position: 'absolute',
                            rotate: placedTile.rotation * 60 + 'deg',
                            left: placedTile.left,
                            top: placedTile.top,
                            width: placedTile.width,
                            height: placedTile.height,
                            filter: 'drop-shadow(0 0 10px ' + (placedTile.action ? 'cyan' : 'red')+')',
                            objectFit: 'contain',
                            cursor: (selectedTile && selectedTile.name === placedTile.name) ? 'crosshair' : 'pointer',
                            userSelect: 'none',
                            zIndex: 5,
                            pointerEvents: 'auto',
                        }}

                    />
                )}
                {placedTile && (
                    <img
                        src={`/public/arknova/buildings/building_anchor_rotate.png`}
                        alt={placedTile.name}
                        style={{
                            position: 'absolute',
                            left: placedTile.left,
                            top: placedTile.top,
                            width: placedTile.width,
                            height: placedTile.height,
                            objectFit: 'contain',
                            cursor: 'none',
                            zIndex: 6,
                            pointerEvents: 'none'
                        }}
                    />
                )}
            </div>

            {
                placedTile && placedTile.action && <div
                    style={{
                        width: '100%',
                        backgroundColor: "#2a2d4a",
                        borderRadius: "8px",
                        display: "flex",
                        marginBottom: "8px",
                        padding: "10px 15px",
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box',
                    }}>
                    <div style={{flex: 1, fontSize: "16px"}}>
                        Placer le bâtiment {placedTile.name} en ({placedTile.i}, {placedTile.j}) ?
                    </div>
                    <div>
                        <Button
                            variant={"contained"}
                            onClick={() => setConfirmAction({
                                action: placedTile.action,
                                message: "Voulez-vous jouer le bâtiment " + placedTile.action.displayName + " ici ?"
                            })}
                            sx={{minWidth: '80px'}}
                        >
                            Confirmer
                        </Button>
                    </div>

                </div>
            }
            <div style={{
                borderTop: '1px solid #ddd',
                padding: 12,
            }}>
                <h3 style={{margin: '0 0 8px 0'}}>Bâtiments disponibles</h3>
                <div style={{display: 'flex', gap: 12, alignItems: 'center', flexFlow: 'row wrap'}}>
                    {availableBuildings.map(name => (
                        <div key={name} style={{
                            width: 120,
                            height: 120,
                            border: '1px dashed #bbb',
                            borderRadius: 8,
                            background: selectedTile?.name === name ? '#1f70ea' : '#191b31',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            cursor: 'pointer'
                        }}
                             onClick={() => {
                                 setPlacedTile(undefined);
                                 setSelectedTile({name, rotation: 0});
                             }}
                        >
                            <img
                                src={`/public/arknova/buildings/building_${name.toLowerCase()}.png`}
                                alt={name}
                                draggable={false}
                                onError={(ev) => console.warn('available building img failed to load', name, ev?.currentTarget?.src)}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                    cursor: 'pointer',
                                    borderRadius: 6
                                }}
                            />
                        </div>
                    ))}
                </div>
            </div>
            {
                state.actions[userId].filter(a => a.class === "action.ActionPass").map(a => <div>
                    <Button
                        variant={"contained"}
                        disabled={!a.ok}
                        onClick={() => setConfirmAction({action: a, message: a.name + " ?"})}
                        sx={{minWidth: '80px'}}
                    >
                        Passer
                    </Button>
                </div>)
            }
            {
                state.actions[userId].filter(a => a.class === "action.ActionGoBack").map(a => <div>
                    <Button
                        variant={"contained"}
                        disabled={!a.ok}
                        onClick={() => setConfirmAction({action: a, message: a.name + " ?"})}
                        sx={{minWidth: '80px'}}
                    >
                        Retour
                    </Button>
                </div>)
            }
        </div>
    )
}
export default BuildingState;
